/**
 * 구독 2채널이 흘리는 프레임을 시나리오에서 만들어 낸다.
 *
 * ⚠ **채널·봉투는 정본 `events.asyncapi.yaml` 을 따른다**(`L1EventStream` IFC-WS-03 ·
 *   `SafetyStream` IFC-WS-04). 새 스타일을 발명하지 않았다 — 서버가 그 계약대로 구현하면
 *   프론트는 이 목을 걷어내기만 하면 된다.
 *
 * 🔴 **서버 미구현이지 계약 없음이 아니다.** WS 라우트가 0건(2026-09-17 실측)이라 목으로 간다.
 *
 * ⚠ **계약이 답하지 않는 것 하나** — 두 채널이 «무소식일 때 keepalive 프레임을 보내는가»가
 *   계약에 없다. 그래서 이 목은 `/safety/stream` 에 아무것도 흘리지 않는다(안전 트리거가 없으면
 *   보낼 것이 없다). 시간 기반 stale 판정의 대상이 무엇인지와 직결되므로 백엔드 확인 사항이다.
 */
import type { L1EventMessage, ModuleSnapshot, SafetyMessage } from '@ctcells/contract'

import type { DemoScenario } from '../scenarios.ts'

function now(): string {
  return new Date().toISOString()
}

function moduleStatus(module: ModuleSnapshot): L1EventMessage {
  return {
    type: 'module_status',
    ts: now(),
    module_id: module.module_id,
    ...(module.version === undefined ? {} : { source_version: module.version }),
    stale: module.stale,
    payload: module,
  }
}

function moduleTelemetry(module: ModuleSnapshot): L1EventMessage {
  return {
    type: 'module_telemetry',
    ts: now(),
    module_id: module.module_id,
    stale: module.stale,
    payload: module.telemetry ?? {},
  }
}

/**
 * 실행 진행 한 건 — **서버가 실제로 내는 모양 그대로다**(코드 3단 대조 2026-09-20).
 *
 * 봉투는 `ctcells-core` `views/stream_relay._execution`, 본문은 `module-core`
 * `core/publisher._publish_execution` 이 만든다. 본문에 있는 것은 `ts`·`type`·`version`·
 * `execution_id`·`step`·`total`(+ 있을 때 `message`)이고 **진행률·경과·편차는 없다.**
 *
 * 🔴 **봉투에 `batch_id` 를 싣지 않는다.** 서버는 모듈 미러의 `execution_id` 가 일치할 때만
 *    싣고, 아니면 **안 싣는다** — 그때 화면이 `module_status.current_execution` 으로 잇는 것이
 *    서버가 설계한 방식이다(`stream_relay._execution` 머리말). 목이 쉬운 쪽(항상 싣기)만
 *    내면 **그 잇기 코드가 시험되지 않는다.** 그래서 어려운 쪽을 낸다.
 */
function batchProgress(
  module: ModuleSnapshot,
  execution: NonNullable<ModuleSnapshot['current_execution']>,
  step: number,
  message: string,
): L1EventMessage {
  return {
    type: 'batch_progress',
    ts: now(),
    module_id: module.module_id,
    ...(module.version === undefined ? {} : { source_version: module.version }),
    payload: {
      ts: now(),
      type: 'progress',
      version: module.version ?? 1,
      execution_id: execution.execution_id,
      step,
      total: execution.total,
      message,
    },
  }
}

/**
 * 모듈 종류별 **동작 차례** — 🔴 **실물 기록에서 관측된 것만**이다(2026-09-20 배치
 * `e1f7fa76-…` · 프레임 20,855건 전수). 지어낸 동작이 하나도 없고, 차례도 실물이 돈 순서다.
 *
 * ⚠ **왜 이것이 필요한가**: demo 가 `message` 없이 같은 스텝만 되풀이하면
 *   「동작 이름」축이 통째로 비어서 **demo 에서만 그림이 안 움직인다.** 그것은
 *   「fixture 가 실물보다 순하다」의 전형이고, 반대로 demo 에만 있는 값을 실으면
 *   **demo 만 자세한 그림**이 된다. 실물이 내는 것과 «같은 만큼»을 낸다.
 *
 * ⚠ 실물 문구 꼴이 `"<동작> 시작"` 이다 — 그 꼴을 그대로 쓴다(`progress.ts` 가 여기서 읽는다).
 */
const DEMO_ACTIONS: Record<string, readonly string[]> = {
  centrifuge: ['move_to', 'pick', 'move_to', 'place', 'inspect', 'run_centrifugation'],
  assembler: ['move_to', 'pick', 'move_to', 'place', 'expose_uv', 'invert_drain'],
  staining: ['move_to', 'mount_tip', 'aspirate', 'dispense_reagent', 'discard_tip', 'shake'],
  collaborative_6axis: ['move_to_module', 'pickup', 'place'],
}

export interface StreamFeed {
  /** 연결 직후 1회 — 연결 알림 + 현재 스냅샷 성격의 프레임 */
  hello(): L1EventMessage[]
  /** 주기 프레임 1건. 호출할 때마다 다음 것 */
  next(): L1EventMessage
}

export function createStreamFeed(scenario: DemoScenario): StreamFeed {
  let cursor = 0

  const frames: (() => L1EventMessage)[] = [
    ...scenario.modules.map((module) => () => moduleTelemetry(module)),
    ...scenario.modules.map((module) => () => moduleStatus(module)),
  ]

  const batch = scenario.batch
  const running = batch
    ? scenario.modules.find((module) => module.current_execution?.batch_id === batch.batch_id)
    : undefined
  const execution = running?.current_execution
  if (batch && running && execution) {
    /*
     * 🔴 **스텝이 나아간다.** 종전에는 같은 `step` 을 영원히 되풀이해서 demo 에서
     *    진행이 멈춰 보였다 — 장비 관점 그림은 스텝을 시계로 삼으므로 그러면 아무것도 안 움직인다.
     * ⚠ **총수를 넘지 않는다.** 넘으면 「96/96 을 지난 스텝」이라는 있을 수 없는 값이 된다 —
     *   끝에 닿으면 처음으로 돌아간다(demo 는 되풀이하는 전시라 그것이 맞다).
     */
    const actions = DEMO_ACTIONS[running.module_type] ?? ['move_to']
    const total = execution.total ?? actions.length
    let step = execution.step ?? 0
    let turn = 0
    frames.push(() => {
      const action = actions[turn % actions.length] ?? 'move_to'
      turn += 1
      step = total > 0 ? (step + 1) % total : step + 1
      return batchProgress(running, execution, step, `${action} 시작`)
    })
  }

  return {
    hello() {
      const hello: L1EventMessage[] = [
        {
          type: 'connection',
          ts: now(),
          payload: { target: 'orchestrator', connected: true },
        },
      ]
      for (const module of scenario.modules) hello.push(moduleStatus(module))
      if (batch)
        hello.push({ type: 'batch_status', ts: now(), batch_id: batch.batch_id, payload: batch })
      for (const alarm of scenario.alarms) hello.push({ type: 'alarm', ts: now(), payload: alarm })
      return hello
    },
    next() {
      const make = frames[cursor % frames.length]
      cursor += 1
      return make
        ? make()
        : { type: 'connection', ts: now(), payload: { target: 'orchestrator', connected: true } }
    },
  }
}

/** 안전 채널 프레임. 트리거가 있을 때만 나간다 — 주기 발행이 아니다 */
export function safetyFrame(type: SafetyMessage['type'], source = 'orchestrator'): SafetyMessage {
  return { type, source, ts: now(), severity: 'CRITICAL' }
}
